'use client'

import dynamic from 'next/dynamic'
import type {JotulWidgetProps} from '@jotul/jotul-widgets'
import {t} from '@/lib/i18n'
import {resolveProductWidgetMarkets} from '@/components/layout/dealerWidgetBrands'

const JotulWidget = dynamic<JotulWidgetProps>(
  () => import('@jotul/jotul-widgets').then((mod) => mod.JotulWidget),
  {ssr: false},
)

type ProductEnquirySheetProps = {
  productId?: string
  productVariant?: string
  productTitle: string
  locale?: string
  market?: string
  markets?: string[]
  brandKey?: string
  brand?: string
  isDefaultSite?: boolean
}

const WIDGET_BUTTON_THEMES: Record<string, {backgroundColor: string; backgroundImage?: string}> = {
  jotul: {
    backgroundColor: '#ef2b18',
    backgroundImage: 'linear-gradient(to right, #f15a22, #ed1d24)',
  },
  scan: {
    backgroundColor: '#ff7f00',
    backgroundImage: 'linear-gradient(to right, #ff9933, #ff7f00)',
  },
  atra: {
    backgroundColor: '#db3832',
    backgroundImage: 'linear-gradient(to right, #e05c57, #db3832)',
  },
  ild: {
    backgroundColor: '#eeaf00',
    backgroundImage: 'linear-gradient(to right, #f2c133, #eeaf00)',
  },
}

const normalizeBrandKey = (value?: string) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()

const resolveWidgetButtonTheme = (brandKey?: string) =>
  WIDGET_BUTTON_THEMES[normalizeBrandKey(brandKey)] || WIDGET_BUTTON_THEMES.jotul

const resolveWidgetLocale = (locale?: string) => {
  const value = String(locale || '').trim()
  return value || 'nb-NO'
}

export function ProductEnquirySheet({
  productId,
  productVariant,
  productTitle,
  locale,
  market,
  markets,
  brandKey,
  brand,
  isDefaultSite,
}: ProductEnquirySheetProps) {
  const brands = brandKey ? [brandKey] : undefined
  const widgetProductId = productId?.trim() || undefined
  const widgetBrand = brand?.trim() || brandKey
  const buttonTheme = resolveWidgetButtonTheme(brandKey)
  const widgetProductVariant = productVariant?.trim() || undefined
  const buttonLabel = t(locale, 'product.findDealer', 'Find dealer')
  const loadingLabel = t(locale, 'common.loading', 'Loading...')
  const buttonClassName =
    'jwi-inline-flex jwi-min-h-12 jwi-w-full sm:jwi-w-auto sm:jwi-min-w-44 jwi-cursor-pointer jwi-items-center jwi-justify-center jwi-rounded-md jwi-border-0 jwi-px-6 jwi-py-3 jwi-text-base jwi-font-bold jwi-leading-none jwi-text-white'

  return (
    <JotulWidget
      type="productPage"
      className="product-enquiry-widget !w-full !max-w-none"
      productId={widgetProductId}
      productVariant={widgetProductVariant}
      productName={productTitle}
      locale={resolveWidgetLocale(locale)}
      markets={resolveProductWidgetMarkets({market, markets, locale, isDefaultSite})}
      brand={widgetBrand}
      brands={brands}
      styling={{
        button: {
          borderRadius: '9999px',
          backgroundColor: buttonTheme.backgroundColor,
          textColor: '#ffffff',
        },
        border: {
          borderColor: buttonTheme.backgroundColor,
        },
      }}
      button={
        <button
          type="button"
          className={buttonClassName}
          style={{
            borderRadius: '9999px',
            backgroundColor: buttonTheme.backgroundColor,
            backgroundImage: buttonTheme.backgroundImage,
            backgroundPosition: 'left',
            backgroundSize: '100% 100%',
            color: '#ffffff',
          }}
        >
          {buttonLabel}
        </button>
      }
      buttonLoading={
        <button
          type="button"
          disabled
          className={`${buttonClassName} jwi-opacity-70`}
          style={{
            borderRadius: '9999px',
            backgroundColor: buttonTheme.backgroundColor,
            backgroundImage: buttonTheme.backgroundImage,
            backgroundPosition: 'left',
            backgroundSize: '100% 100%',
            color: '#ffffff',
          }}
        >
          {loadingLabel}
        </button>
      }
    />
  )
}
